import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

/**
 * HomePage - Landing page with getting started guide and DPDP glossary
 */

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8faff 0%, #eef2ff 50%, #e0e7ff 100%);
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
`;

const Header = styled.header`
  padding: 20px 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  .icon {
    width: 40px;
    height: 40px;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 800;
    font-size: 20px;
  }
  
  .text {
    font-size: 20px;
    font-weight: 700;
    color: #1e1e2e;
  }
`;

const NavButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button<{ $primary?: boolean }>`
  padding: 10px 24px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.$primary ? `
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    border: none;
    color: white;
    box-shadow: 0 4px 16px rgba(99, 102, 241, 0.35);
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 24px rgba(99, 102, 241, 0.45);
    }
  ` : `
    background: white;
    border: 2px solid #e5e7eb;
    color: #374151;
    
    &:hover {
      border-color: #6366f1;
      color: #6366f1;
    }
  `}
`;

const Main = styled.main`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 24px;
`;

const HeroSection = styled.section`
  text-align: center;
  margin-bottom: 60px;
  animation: ${fadeInUp} 0.6s ease-out;
`;

const HeroIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%);
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 800;
  font-size: 36px;
  margin: 0 auto 24px;
  box-shadow: 0 8px 32px rgba(99, 102, 241, 0.35);
  animation: ${float} 3s ease-in-out infinite;
`;

const HeroTitle = styled.h1`
  font-size: 42px;
  font-weight: 800;
  color: #1e1e2e;
  margin: 0 0 16px;
  letter-spacing: -1px;
`;

const HeroSubtitle = styled.p`
  font-size: 18px;
  color: #6b7280;
  max-width: 600px;
  margin: 0 auto 32px;
  line-height: 1.6;
`;

const ComplianceBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 30px;
  font-size: 14px;
  font-weight: 600;
  color: #6366f1;
`;

const SectionTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #1e1e2e;
  margin: 0 0 8px;
  text-align: center;
`;

const SectionSubtitle = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin: 0 0 32px;
  text-align: center;
`;

const GettingStartedSection = styled.section`
  margin-bottom: 60px;
`;

const StepsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const StepCard = styled.div<{ $completed?: boolean }>`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  border: 2px solid ${props => props.$completed ? '#10b981' : 'transparent'};
  position: relative;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  }
  
  .step-number {
    position: absolute;
    top: -12px;
    left: 20px;
    width: 32px;
    height: 32px;
    background: ${props => props.$completed ? '#10b981' : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 700;
    font-size: 14px;
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
  }
  
  .step-icon {
    font-size: 32px;
    margin-bottom: 12px;
  }
  
  .step-title {
    font-size: 18px;
    font-weight: 700;
    color: #1e1e2e;
    margin: 0 0 8px;
  }
  
  .step-description {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.5;
    margin: 0 0 16px;
  }
  
  .step-action {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    background: #f8f9fa;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    color: #6366f1;
    cursor: pointer;
    transition: all 0.2s;
    
    &:hover {
      background: #eef2ff;
    }
  }
`;

const Connector = styled.div`
  display: none;
  
  @media (min-width: 1025px) {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    right: -16px;
    top: 50%;
    transform: translateY(-50%);
    color: #d1d5db;
    font-size: 20px;
  }
`;

const GlossarySection = styled.section`
  margin-bottom: 60px;
`;

const GlossaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const GlossaryCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border-left: 4px solid #6366f1;
  
  .term {
    font-size: 16px;
    font-weight: 700;
    color: #1e1e2e;
    margin: 0 0 8px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .definition {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.6;
    margin: 0;
  }
  
  .dpdp-ref {
    display: inline-block;
    margin-top: 8px;
    font-size: 11px;
    color: #9ca3af;
    background: #f8f9fa;
    padding: 4px 8px;
    border-radius: 4px;
  }
`;

const QuickActionsSection = styled.section`
  background: white;
  border-radius: 20px;
  padding: 32px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
`;

const ActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ActionCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  background: linear-gradient(135deg, #f8faff 0%, #eef2ff 100%);
  border: 2px solid transparent;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: #6366f1;
    transform: translateY(-2px);
  }
  
  .action-icon {
    font-size: 32px;
    margin-bottom: 12px;
  }
  
  .action-title {
    font-size: 16px;
    font-weight: 600;
    color: #1e1e2e;
    margin: 0 0 4px;
  }
  
  .action-description {
    font-size: 13px;
    color: #6b7280;
    margin: 0;
  }
`;

const Footer = styled.footer`
  padding: 32px;
  text-align: center;
  border-top: 1px solid #e5e7eb;
  margin-top: 60px;
`;

const FooterLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 32px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const FooterLink = styled.a`
  color: #6b7280;
  font-size: 14px;
  text-decoration: none;
  transition: color 0.2s;
  
  &:hover {
    color: #6366f1;
  }
`;

const DPDP_ACT_URL = 'https://www.meity.gov.in/static/uploads/2025/11/53450e6e5dc0bfa85ebd78686cadad39.pdf';

const GLOSSARY_TERMS = [
  {
    icon: '👤',
    term: 'Data Principal',
    definition: 'The individual whose personal data is being processed. In the DPDP Act, this refers to the person who provides consent.',
    dpdpRef: 'Section 2(j)',
  },
  {
    icon: '🏢',
    term: 'Data Fiduciary',
    definition: 'An entity that determines the purpose and means of processing personal data. They are responsible for ensuring lawful processing.',
    dpdpRef: 'Section 2(i)',
  },
  {
    icon: '📋',
    term: 'Consent',
    definition: 'A free, specific, informed, unconditional, and unambiguous indication of the Data Principal\'s wishes.',
    dpdpRef: 'Section 6',
  },
  {
    icon: '📝',
    term: 'Consent Template',
    definition: 'A reusable document structure that defines the consent text, data categories, and collection requirements for a specific purpose.',
    dpdpRef: 'Platform-specific',
  },
  {
    icon: '📁',
    term: 'Project',
    definition: 'A grouping of related consent templates, typically representing a specific use case or application within an organization.',
    dpdpRef: 'Platform-specific',
  },
  {
    icon: '🔐',
    term: 'Evidence',
    definition: 'Proof that consent was given, such as digital signature, OTP verification, or video recording.',
    dpdpRef: 'Section 6(1)',
  },
  {
    icon: '📊',
    term: 'Data Categories',
    definition: 'Types of personal data being collected, such as contact information, health data, or financial information.',
    dpdpRef: 'Section 3(1)',
  },
  {
    icon: '⏱️',
    term: 'Retention Period',
    definition: 'The duration for which personal data is stored. Data must be deleted when consent is withdrawn or purpose is fulfilled.',
    dpdpRef: 'Section 8(7)',
  },
];

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [isLoggedIn] = useState(!!localStorage.getItem('authToken'));

  return (
    <PageWrapper>
      <Header>
        <Logo>
          <div className="icon">T</div>
          <div className="text">Consent Platform</div>
        </Logo>
        <NavButtons>
          {isLoggedIn ? (
            <Button $primary onClick={() => navigate('/admin/dashboard')}>
              Go to Dashboard →
            </Button>
          ) : (
            <>
              <Button onClick={() => window.open(DPDP_ACT_URL, '_blank')}>
                📜 DPDP Act
              </Button>
              <Button $primary onClick={() => navigate('/login')}>
                Sign In →
              </Button>
            </>
          )}
        </NavButtons>
      </Header>

      <Main>
        <HeroSection>
          <HeroIcon>T</HeroIcon>
          <HeroTitle>Consent Management Made Simple</HeroTitle>
          <HeroSubtitle>
            Collect, manage, and track user consent in compliance with India's Digital Personal Data Protection Act 2023.
          </HeroSubtitle>
          <ComplianceBadge>
            <span>🇮🇳</span>
            DPDP Act 2023 Compliant
          </ComplianceBadge>
        </HeroSection>

        <GettingStartedSection>
          <SectionTitle>🚀 Getting Started</SectionTitle>
          <SectionSubtitle>Follow these steps to start collecting consent</SectionSubtitle>
          
          <StepsContainer>
            <StepCard style={{ position: 'relative' }}>
              <div className="step-number">1</div>
              <div className="step-icon">🏢</div>
              <h3 className="step-title">Create Data Collector</h3>
              <p className="step-description">
                Register your organization as a Data Fiduciary with contact details, DPO information, and grievance officer.
              </p>
              <div className="step-action" onClick={() => navigate('/admin/data-collectors')}>
                Add Organization →
              </div>
              <Connector>→</Connector>
            </StepCard>
            
            <StepCard style={{ position: 'relative' }}>
              <div className="step-number">2</div>
              <div className="step-icon">📁</div>
              <h3 className="step-title">Create Project</h3>
              <p className="step-description">
                Define a project to group related consent templates. Specify the data categories you'll collect.
              </p>
              <div className="step-action" onClick={() => navigate('/admin/projects')}>
                Create Project →
              </div>
              <Connector>→</Connector>
            </StepCard>
            
            <StepCard style={{ position: 'relative' }}>
              <div className="step-number">3</div>
              <div className="step-icon">📋</div>
              <h3 className="step-title">Design Template</h3>
              <p className="step-description">
                Create consent templates with customizable text, branding, and evidence collection methods.
              </p>
              <div className="step-action" onClick={() => navigate('/admin/templates')}>
                Build Template →
              </div>
              <Connector>→</Connector>
            </StepCard>
            
            <StepCard>
              <div className="step-number">4</div>
              <div className="step-icon">🔗</div>
              <h3 className="step-title">Generate & Send</h3>
              <p className="step-description">
                Generate consent URLs and send them to data owners via email, SMS, or embed in your application.
              </p>
              <div className="step-action" onClick={() => navigate('/admin/consent-forms')}>
                Generate URL →
              </div>
            </StepCard>
          </StepsContainer>
        </GettingStartedSection>

        <GlossarySection>
          <SectionTitle>📚 DPDP Act Glossary</SectionTitle>
          <SectionSubtitle>Key terms and concepts from the Digital Personal Data Protection Act 2023</SectionSubtitle>
          
          <GlossaryGrid>
            {GLOSSARY_TERMS.map((term, index) => (
              <GlossaryCard key={index}>
                <h4 className="term">
                  <span>{term.icon}</span>
                  {term.term}
                </h4>
                <p className="definition">{term.definition}</p>
                <span className="dpdp-ref">{term.dpdpRef}</span>
              </GlossaryCard>
            ))}
          </GlossaryGrid>
        </GlossarySection>

        <QuickActionsSection>
          <SectionTitle style={{ marginBottom: '24px' }}>⚡ Quick Actions</SectionTitle>
          
          <ActionsGrid>
            <ActionCard onClick={() => navigate('/admin/dashboard')}>
              <span className="action-icon">📊</span>
              <span className="action-title">Dashboard</span>
              <span className="action-description">View analytics and stats</span>
            </ActionCard>
            
            <ActionCard onClick={() => navigate('/admin/consent-records')}>
              <span className="action-icon">📝</span>
              <span className="action-title">Consent Artifacts</span>
              <span className="action-description">View collected consents</span>
            </ActionCard>
            
            <ActionCard onClick={() => window.open(DPDP_ACT_URL, '_blank')}>
              <span className="action-icon">📜</span>
              <span className="action-title">DPDP Act PDF</span>
              <span className="action-description">Official government document</span>
            </ActionCard>
          </ActionsGrid>
        </QuickActionsSection>
      </Main>

      <Footer>
        <FooterLinks>
          <FooterLink href={DPDP_ACT_URL} target="_blank" rel="noopener noreferrer">
            DPDP Act 2023
          </FooterLink>
          <FooterLink href="mailto:support@tashi.in">
            Support
          </FooterLink>
          <FooterLink href="/admin/dashboard">
            Dashboard
          </FooterLink>
        </FooterLinks>
        <p style={{ color: '#9ca3af', fontSize: '13px', margin: 0 }}>
          © {new Date().getFullYear()} Consent Platform Management. All rights reserved.
        </p>
      </Footer>
    </PageWrapper>
  );
};

export default HomePage;
